import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
  Fade,
} from "@mui/material";
import Swal from "sweetalert2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/system";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../Components/helpers/Helpers";

import { useContext } from "react";
import { UserContext } from "../../Components/Context/Context";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";

const Editar = () => {
  const navigate = useNavigate();

  const { goPanel, goView, goTable, buildViewUrl, buildTableUrl } =
    useLocationHelpers();
  const [selectedFile, setSelectedFile] = useState("");
  const [optionsProvinces, setOptionsProvinces] = useState([]);

  const [loadingOptionsProvinces, setLoadingOptionsProvinces] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("id");

  const { updateCompanyImage, updateCompanyImageRealTime } =
    useContext(UserContext); // Usa el contexto

  const [companyData, setCompanyData] = useState({
    companyEmail: "",
    companyName: "",
    companyProvince: null, // Cambiado a null para manejar Autocomplete
    companyAddress: "",
    companyPostalCode: "",
    companyCif: "",
    companyPhone: "",
    companyContactPerson: "",
    companyLogo: "",
  });

  // Fetch data for the form
  const getData = async () => {
    console.log(companyId);
    setLoading(false);

    const parametros = {
      company_id: companyId,
    };

    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/companies/edit`,
        parametros,
      );

      console.log(response);

      setOptionsProvinces([
        { provincia: "Seleccionar", id: 0 },
        ...response.data.provinces,
      ]);

      const {
        id,
        company_email,
        company_name,
        provinces_relation,
        company_address,
        company_postalcode,
        company_cif,
        company_phone,
        company_contactname,
        company_logo,
      } = response.data.company;

      setCompanyData({
        company_id: id,
        companyEmail: company_email,
        companyName: company_name,
        companyProvince: provinces_relation,
        companyAddress: company_address,
        companyPostalCode: company_postalcode,
        companyCif: company_cif,
        companyPhone: company_phone,
        companyContactPerson: company_contactname,
        companyLogo: company_logo,
      });

      setLoading(true);
      setSelectedFile({ name: company_logo });
    } catch (error) {
      setLoading(true);
      console.log(error);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // Form submit handler
  const handleCompaniesFormInformation = async () => {
    const isValid = validateFields();

    if (isValid) {
      if (!selectedFile && companyData.companyLogo == "") {
        alert("Por favor selecciona un archivo");
        return;
      }

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("file_name", selectedFile.name);
      // Append form data
      formData.append("company_id", companyId);
      formData.append("company_email", companyData.companyEmail);
      formData.append("company_name", companyData.companyName);
      formData.append(
        "company_province",
        companyData.companyProvince?.provincia,
      );
      formData.append("company_address", companyData.companyAddress);
      formData.append("company_postalcode", companyData.companyPostalCode);
      formData.append("company_cif", companyData.companyCif);
      formData.append("company_phone", companyData.companyPhone);
      formData.append("company_contactname", companyData.companyContactPerson);

      const queryUrl = `${process.env.REACT_APP_API_URL}/companies/update`;

      Swal.fire({
        title: "¿Desea guardar los cambios?",
        showDenyButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Guardar",
        denyButtonText: `Volver`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await api.post(queryUrl, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });

            if (!response.data.error) {
              var { company } = response.data;
              updateCompanyImageRealTime(company?.company_logo); // Actualiza la imagen despuí©s de guardar
            }

            if (response.data.error) {
              Swal.fire({
                title: "¡No se guardaron los cambios!",
                icon: "error",
                text: "Por favor intentelo nuevamente",
                confirmButtonColor: "#1ab394",
              });
            } else {
              Swal.fire({
                title: "¡Guardado!",
                icon: "success",
                showDenyButton: false,
                confirmButtonColor: "#1ab394",
                confirmButtonText: "Continuar",
              }).then((result) => {
                if (result.isConfirmed) {
                  navigate("/empresas/ver?id=" + company?.id);
                }
              });
            }
          } catch (error) {
            console.log(error);
          }
        } else if (result.isDenied) {
          Swal.fire("¡Los cambios no han sido guardados!", "", "info");
        }
      });
    } else {
      showValidationErrors();
    }
  };

  // Validate required fields
  const validateFields = () => {
    const fields = [
      { value: companyData.companyName, name: "Nombre" },
      { value: companyData.companyCif, name: "CIF" },
      { value: companyData.companyAddress, name: "Dirección" },
      { value: companyData.companyProvince, name: "Provincia" },
      { value: companyData.companyPostalCode, name: "Código postal" },
      { value: companyData.companyContactPerson, name: "Persona de contacto" },
      { value: companyData.companyEmail, name: "Email" },
      { value: companyData.companyPhone, name: "Teléfono" },
    ];

    return fields.every((field) => field.value !== "" && field.value !== null);
  };

  const showValidationErrors = () => {
    const fields = [
      { value: companyData.companyName, name: "Nombre" },
      { value: companyData.companyCif, name: "CIF" },
      { value: companyData.companyAddress, name: "Dirección" },
      { value: companyData.companyProvince, name: "Provincia" },
      { value: companyData.companyPostalCode, name: "Código postal" },
      { value: companyData.companyContactPerson, name: "Persona de contacto" },
      { value: companyData.companyEmail, name: "Email" },
      { value: companyData.companyPhone, name: "Teléfono" },
    ];

    const missingFields = fields
      .filter((field) => field.value === "" || field.value === null)
      .map((field) => `<li>${field.name}</li>`)
      .join("");

    Swal.fire({
      title: "¡Error!",
      icon: "error",
      html: `<div style="text-align: start;">Por favor rellene los campos: <ul>${missingFields}</ul></div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  const handleChange = (event) => {
    setCompanyData({
      ...companyData,
      [event.target.name]: event.target.value.trim(),
    });
  };

  const handleAutocompleteChange = (event, value) => {
    console.log(value);
    setCompanyData({
      ...companyData,
      companyProvince: value,
    });
  };

  // Fields for the form
  const formFields = [
    { name: "companyName", label: "Nombre", key: "companyName", type: "text" },
    { name: "companyCif", label: "CIF", key: "companyCif", type: "text" },
    {
      name: "companyAddress",
      label: "Dirección",
      key: "companyAddress",
      type: "text",
    },
    {
      name: "companyPostalCode",
      label: "Código postal",
      key: "companyPostalCode",
      type: "text",
    },
    {
      name: "companyContactPerson",
      label: "Persona de contacto",
      key: "companyContactPerson",
      type: "text",
    },
    { name: "companyEmail", label: "Email", key: "companyEmail", type: "text" },
    {
      name: "companyPhone",
      label: "Teléfono",
      key: "companyPhone",
      type: "text",
    },
    {
      label: "Provincia",
      name: "companyProvince",
      key: "companyProvince",
      type: "autocomplete",
      options: optionsProvinces,
      loading: loadingOptionsProvinces,
      isOptionEqualToValue: (option, value) =>
        option.provincia === value.provincia,
      getOptionLabel: (option) => option.provincia,
    },
  ];

  // Input file upload styled component
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    whiteSpace: "nowrap",
    width: 1,
  });

  const InputFileUpload = ({ text }) => (
    <Button
      component="label"
      startIcon={<CloudUploadIcon />}
      sx={{
        border: "solid",
        "& .MuiSvgIcon-root": { fontSize: "25px !important" },
      }}>
      {text}
      <VisuallyHiddenInput type="file" onChange={handleFileChange} />
    </Button>
  );

  // Handle file upload
  const handleFileChange = (event) => {
    const files = event.target.files;
    if (
      files.length > 0 &&
      (files[0].type === "image/jpeg" || files[0].type === "image/png")
    ) {
      setSelectedFile(files[0]);
    } else {
      Swal.fire({
        title: "¡Error!",
        icon: "error",
        html: "Sólo se permite imágenes en formato PNG o JPG",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  const ImageCell = () => {
    const { companyImage } = useContext(UserContext);

    console.log("Archivo seleccionado:", selectedFile);
    console.log("Imagen en contexto:", companyImage);

    let imageUrl = "";

    if (selectedFile instanceof File) {
      // Imagen cargada desde el input
      imageUrl = URL.createObjectURL(selectedFile);
    } else if (companyImage instanceof File) {
      // Imagen guardada en el contexto
      imageUrl = URL.createObjectURL(companyImage);
    } else if (companyData.companyLogo) {
      // Imagen existente del servidor
      imageUrl = `${process.env.REACT_APP_BACKEND_HOST}/storage/company/${companyId}/${companyData.companyLogo}`;
    }

    return <img src={imageUrl} alt="Company logo" className="image-views" />;
  };

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Empresas" subTitle="Editar" />

      <Fade in={loading} timeout={800}>
        <Box
          component="form"
          sx={{ margin: "auto", "& > :not(style)": {} }}
          noValidate
          autoComplete="off"
          className="formMainContainer">
          <Grid
            container
            style={{ textAlign: "center" }}
            p={2}
            className="fontSize">
            {formFields.map((field, idx) =>
              field.type === "autocomplete" ? (
                <Grid item xs={12} sm={6} key={idx} sx={{ padding: "5px" }}>
                  <Autocomplete
                    value={companyData[field.name]}
                    onChange={handleAutocompleteChange}
                    options={field.options}
                    loading={field.loading}
                    isOptionEqualToValue={field.isOptionEqualToValue}
                    getOptionLabel={field.getOptionLabel}
                    renderInput={(params) => (
                      <TextField {...params} label={field.label} fullWidth />
                    )}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} key={idx} sx={{ padding: "5px" }}>
                  <TextField
                    name={field.name}
                    label={field.label}
                    value={companyData[field.name]}
                    onChange={handleChange}
                    fullWidth
                    inputProps={{
                      maxLength:
                        field.name === "companyPostalCode" ? 10 : undefined,
                    }}
                  />
                </Grid>
              ),
            )}
            <Grid
              container
              item
              xs={12}
              sx={{ padding: "5px", textAlign: "center" }}>
              <Grid item xs={12} className="image-view-container">
                <ImageCell />
              </Grid>
              <Grid item xs={12} sx={{ marginY: "10px" }}>
                <FormLabel>
                  {selectedFile.name
                    ? "Archivo seleccionado: " + selectedFile.name
                    : ""}
                </FormLabel>
              </Grid>
              <Grid item xs={12} sx={{ marginY: "10px" }}>
                <InputFileUpload text="Subir Logo" />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ButtonGroup
                sx={{
                  width: "100%",
                  justifyContent: "center",
                  textAlign: "center",
                  margin: "20px auto 0px auto",
                }}>
                <Button
                  sx={{ width: "100%" }}
                  onClick={handleCompaniesFormInformation}
                  variant="outlined"
                  color="success">
                  Guardar
                </Button>
                <NavButton
                  sx={{ width: "100%" }}
                  to={buildViewUrl("empresa", companyId)}
                  onClick={() => goView("empresa", companyId)}
                  variant="outlined">
                  Ver
                </NavButton>
                <NavButton
                  sx={{ width: "100%" }}
                  to={buildTableUrl("empresas")}
                  onClick={() => goTable("empresas", "Empresas")}
                  variant="outlined"
                  color="error">
                  Tabla
                </NavButton>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </div>
  );
};

export default Editar;
